import React from "react"
import AvatarFallback from "./avatar-fallback"
import AvatarImage from "./avatar-image"
import { AvatarSize } from "./types"

const getInitials = (name?: string) => {
  if (!name) return ""

  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase()
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export type { AvatarSize }

export type AvatarProps = {
  /** URL for the avatar image (displays image when provided) */
  avatarUrl?: string
  /** User's display name (used for initials in fallback) */
  name?: string
  /** Additional CSS classes to apply to the avatar */
  className?: string
  /** Whether the avatar is active (dims when false) */
  active?: boolean
  /** Size of the avatar */
  size?: AvatarSize
  "data-slot"?: string
}

/**
 * Displays a user avatar as an image or colored initials fallback.
 * Supports two sizes and an active/inactive state.
 */
const Avatar = ({
  avatarUrl,
  name,
  className,
  active = true,
  size = "sm",
  "data-slot": dataSlot,
}: AvatarProps) => {
  if (avatarUrl) {
    return (
      <div className="relative" data-slot={dataSlot}>
        <AvatarImage
          src={avatarUrl}
          alt={name}
          className={className}
          size={size}
        />
        {!active && (
          <div className="absolute inset-0 bg-gray-700 opacity-50 rounded-full" />
        )}
      </div>
    )
  }

  const initials = getInitials(name)

  return (
    <div className="relative" data-slot={dataSlot}>
      <AvatarFallback name={name} className={className} size={size}>
        {initials}
      </AvatarFallback>
      {!active && (
        <div className="absolute inset-0 bg-gray-700 opacity-50 rounded-full" />
      )}
    </div>
  )
}

export { Avatar, AvatarFallback, AvatarImage }
export default Avatar
